import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowUpRight, Package, ClockCounterClockwise, CheckCircle, Warning, Broom, Lightning, Sliders, TrayArrowDown, Hourglass, XCircle, MagnifyingGlass, FireSimple, Plus, ListMagnifyingGlass, ArrowClockwise as ArrowClockwiseIcon, Truck } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import BulkQuotaAdjustDialog from "@/components/BulkQuotaAdjustDialog";
import BulkPasswordDialog from "@/components/BulkPasswordDialog";
import AgingPill from "@/components/AgingPill";
import { computeAging, reqAgingSince } from "@/lib/aging";
import { fmtDateTime, fmtDateShort } from "@/lib/dates";

function StatCard({ label, value, hint, icon: Icon, testid }) {
  return (
    <div className="border border-neutral-200 bg-white p-6 hover:border-neutral-900 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display text-5xl mt-4 leading-none tracking-tight">{value}</div>
          {hint && <div className="text-xs text-neutral-500 mt-3">{hint}</div>}
        </div>
        <Icon size={22} weight="duotone" className="text-neutral-900" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [inventory, setInventory] = useState(null);   // {in_hand, pending_return} — engineer only
  const [openIVs, setOpenIVs] = useState([]);         // engineer-only: their own open records
  const [agingReqs, setAgingReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyOp, setBusyOp] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdLog, setPwdLog] = useState(null);
  const isManager = hasRole("manager");
  const isEngineer = hasRole("engineer");
  const isStorekeeper = hasRole("storekeeper");
  const [approvedDOs, setApprovedDOs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      // `/records?page=1&size=6` returns the recent-6 envelope. Engineers also
      // need their open IVs for the "submit return" tile — fetched separately
      // (status=open, scoped to self by backend). Pagination keeps the main
      // Dashboard payload small even when the collection grows.
      const calls = [
        api.get("/stats"),
        api.get("/records", { params: { page: 1, size: 6 } }),
      ];
      if (isEngineer) {
        calls.push(api.get("/requests", { params: { page: 1, size: 20 } }));
        calls.push(api.get(`/engineers/${user.id}/inventory-breakdown`));
        calls.push(api.get("/records", { params: { page: 1, size: 100, status: "open" } }));
      }
      if (isManager) {
        calls.push(api.get("/requests", { params: { status: "pending", page: 1, size: 20 } }));
        calls.push(api.get("/requests", { params: { status: "verified", page: 1, size: 20 } }));
      }
      // Storekeeper + Manager: DOs awaiting release (approved status only).
      if (isStorekeeper || isManager) {
        calls.push(api.get("/delivery-orders", { params: { status: "approved" } }));
      }
      const results = await Promise.all(calls);
      // Helper — paginated endpoints return `{items, total,...}`, legacy ones an array.
      const toItems = (res) => Array.isArray(res?.data) ? res.data : (res?.data?.items || []);

      setStats(results[0].data);
      setRecent(toItems(results[1]).slice(0, 6));
      let idx = 2;
      if (isEngineer) {
        setMyRequests(toItems(results[idx]).slice(0, 20)); idx++;
        setInventory(results[idx].data || null); idx++;
        // Engineer's own open IVs — comes from the dedicated status=open call.
        const openList = toItems(results[idx]).filter(
          (r) => r.status === "open" && (r.return_stage === "none" || !r.return_stage)
        );
        setOpenIVs(openList); idx++;
      }
      if (isManager) {
        const combined = [...toItems(results[idx]), ...toItems(results[idx + 1])];
        setAgingReqs(combined); idx += 2;
      }
      if (isStorekeeper || isManager) {
        setApprovedDOs(toItems(results[idx]) || []);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadPwdLog = async () => {
    if (!isManager) return;
    try {
      const { data } = await api.get("/engineers/bulk-password-log");
      setPwdLog(data?.last || null);
    } catch { /* silent */ }
  };
  useEffect(() => { loadPwdLog(); }, [isManager]);

  const wipeQuotas = async () => {
    if (!window.confirm(
      "⚠ HARD WIPE ALL QUOTAS\n\nThis clears:\n• Every technician's per-part quota row\n• Every quota template\n• Every fallback max_quota cap on user records\n\nAfter this, technicians have NO caps at all until you set new ones.\n\nProceed?"
    )) return;
    setBusyOp("wipe");
    try {
      const res = await api.post("/quota-templates/wipe-all");
      const { quotas_deleted, templates_deleted, max_quota_cleared } = res.data;
      toast.success(
        `Cleared ${quotas_deleted.toLocaleString()} quota row(s), ${templates_deleted} template(s), ` +
        `and ${max_quota_cleared} user max_quota cap(s). Fresh slate.`,
      );
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyOp(""); }
  };

  const blanketSet = async () => {
    if (!window.confirm(
      "🚀 BLANKET-SET QUOTA = 10\n\nGives every technician a cap of 10 units for EVERY part on the master list (all 2,000+ parts).\n\nExisting quotas are updated to 10. You can adjust individual technicians afterwards from the Technicians page.\n\nProceed?"
    )) return;
    setBusyOp("blanket");
    const t = toast.loading("Seeding quotas across all technicians × all parts…");
    try {
      const res = await api.post("/engineers/part-quotas/blanket-set?max_qty=10");
      toast.dismiss(t);
      toast.success(
        `Done: ${res.data.engineers} technician(s) × ${res.data.parts} parts. ` +
        `Created ${res.data.created}, updated ${res.data.updated}. Cap = 10 units per part.`
      );
    } catch (e) { toast.dismiss(t); toast.error(formatApiError(e)); }
    finally { setBusyOp(""); }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className={`border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10 ${isEngineer ? "hidden md:block" : ""}`}>
        <div className="px-10 py-6 flex items-end justify-between">
          <div>
            <div className="label-eyebrow">Overview</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Dashboard</h1>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Session</div>
            <div className="font-mono text-sm mt-1">{user.name} · {user.role}</div>
          </div>
        </div>
      </div>

      {/* Engineer mobile hero — big touch targets, hidden on md+ */}
      {isEngineer && (
        <section
          className="md:hidden px-5 pt-5 pb-2 space-y-4"
          data-testid="engineer-mobile-hero"
        >
          <div>
            <div className="label-eyebrow">Welcome</div>
            <h1 className="font-display text-3xl tracking-tight leading-none mt-2 truncate">
              Hi, {user.name.split(" ")[0]}
            </h1>
            <p className="text-sm text-neutral-500 mt-2">
              Raise a parts request or check the status of the ones you've already sent.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <Link
              to="/requests?new=1"
              data-testid="mobile-request-part-btn"
              className="flex items-center justify-between bg-neutral-900 text-white p-5 border-2 border-neutral-900 active:scale-[0.99] transition-transform"
            >
              <div>
                <div className="label-eyebrow text-neutral-400">Action</div>
                <div className="font-display text-2xl tracking-tight mt-1">Request Part</div>
                <div className="text-xs text-neutral-400 mt-1">Raise a new REQ for your work order</div>
              </div>
              <Plus size={32} weight="bold" />
            </Link>
            <a
              href="#my-requests"
              data-testid="mobile-view-requests-btn"
              className="flex items-center justify-between bg-white text-neutral-900 p-5 border-2 border-neutral-900 active:scale-[0.99] transition-transform"
            >
              <div>
                <div className="label-eyebrow">Status</div>
                <div className="font-display text-2xl tracking-tight mt-1">View My Requests</div>
                <div className="text-xs text-neutral-500 mt-1">Track pending, verified & ready-for-pickup</div>
              </div>
              <ListMagnifyingGlass size={32} weight="bold" />
            </a>
          </div>
        </section>
      )}

      <div className={isEngineer ? "px-5 py-5 md:px-10 md:py-10 space-y-6 md:space-y-10" : "p-10 space-y-10"}>
        {/* Stats — hidden on mobile for engineers (they get the hero above instead) */}
        <section
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 ${isEngineer ? "hidden md:grid" : ""}`}
          data-testid="stats-grid"
        >
          <StatCard label="Total Records" value={stats?.total_records ?? "—"} hint="All time issued" icon={Package} testid="stat-total" />
          <StatCard label="Open" value={stats?.open_records ?? "—"} hint="Awaiting return" icon={ClockCounterClockwise} testid="stat-open" />
          <StatCard label="Returned" value={stats?.returned_records ?? "—"} hint="Closed records" icon={CheckCircle} testid="stat-returned" />
          <StatCard label="Parts Used" value={stats?.parts_used ?? "—"} hint="Consumed on job" icon={CheckCircle} testid="stat-used" />
          <StatCard label="Parts Unused" value={stats?.parts_unused ?? "—"} hint="Returned intact" icon={Warning} testid="stat-unused" />
        </section>

        {/* Technician-only: My Requests status tracker */}
        {isEngineer && <MyInventoryCard inventory={inventory} loading={loading} />}
        {isEngineer && <MyOpenIVsCard records={openIVs} loading={loading} />}
        {isEngineer && <MyRequestsTracker items={myRequests} loading={loading} />}

        {/* Manager-only: Aging REQ card (Verify SLA Timer) */}
        {isManager && <ManagerAgingCard items={agingReqs} loading={loading} />}

        {/* Storekeeper + Manager: DOs awaiting release (Ready to Release queue) */}
        {(isStorekeeper || isManager) && (
          <ReadyToReleaseCard items={approvedDOs} loading={loading} storekeeper={isStorekeeper} />
        )}

        {/* Manager-only quota shortcuts */}
        {isManager && (
          <section data-testid="manager-quota-tools">
            <div className="label-eyebrow mb-3">Manager Tools · Quotas</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-neutral-900 bg-white p-6 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Lightning size={18} weight="duotone" className="text-emerald-700" />
                    <div className="font-display text-lg tracking-tight">Set blanket 10-per-part</div>
                  </div>
                  <div className="text-sm text-neutral-600 mt-1 max-w-md">
                    Give every technician a cap of <b>10 units</b> for every part on the master list. One click, updates all existing quotas too.
                  </div>
                </div>
                <button
                  onClick={blanketSet}
                  disabled={!!busyOp}
                  data-testid="dash-blanket-set"
                  className="shrink-0 rounded-none bg-neutral-900 text-white hover:bg-neutral-800 text-xs font-mono uppercase tracking-wider px-4 py-2 disabled:opacity-40"
                >
                  {busyOp === "blanket" ? "Running…" : "Set now"}
                </button>
              </div>

              <div className="border border-neutral-300 bg-white p-6 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Sliders size={18} weight="duotone" className="text-blue-700" />
                    <div className="font-display text-lg tracking-tight">Bulk adjust</div>
                  </div>
                  <div className="text-sm text-neutral-600 mt-1 max-w-md">
                    Pick specific technicians (e.g. senior techs) and specific parts (e.g. high-turnover) — set a custom cap in one submit.
                  </div>
                </div>
                <button
                  onClick={() => setBulkOpen(true)}
                  disabled={!!busyOp}
                  data-testid="dash-bulk-adjust"
                  className="shrink-0 rounded-none border border-neutral-900 hover:bg-neutral-900 hover:text-white text-xs font-mono uppercase tracking-wider px-4 py-2 disabled:opacity-40"
                >
                  Open panel
                </button>
              </div>

              <div className="border border-neutral-300 bg-white p-6 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Broom size={18} weight="duotone" className="text-red-700" />
                    <div className="font-display text-lg tracking-tight">Wipe all quotas</div>
                  </div>
                  <div className="text-sm text-neutral-600 mt-1 max-w-md">
                    Clear every per-part quota and every template. Use before rebuilding from scratch. Requires confirmation.
                  </div>
                </div>
                <button
                  onClick={wipeQuotas}
                  disabled={!!busyOp}
                  data-testid="dash-wipe-quotas"
                  className="shrink-0 rounded-none border border-red-600 text-red-700 hover:bg-red-600 hover:text-white text-xs font-mono uppercase tracking-wider px-4 py-2 disabled:opacity-40"
                >
                  {busyOp === "wipe" ? "Wiping…" : "Wipe now"}
                </button>
              </div>
            </div>
          </section>
        )}

        <BulkQuotaAdjustDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onSaved={() => {}}
        />

        {/* Manager-only: Technician password tools */}
        {isManager && (
          <section data-testid="manager-password-tools">
            <div className="label-eyebrow mb-3">Manager Tools · Technician Passwords</div>
            <div className="border border-neutral-900 bg-white p-6 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Lightning size={18} weight="duotone" className="text-amber-600" />
                  <div className="font-display text-lg tracking-tight">Bulk Set Passwords</div>
                </div>
                <div className="text-sm text-neutral-600 mt-1 max-w-xl">
                  Apply the same password to every technician in one click (default <b>Tech@123</b>, editable), or upload a CSV to set a unique password per technician. Existing passwords will be overwritten.
                </div>
                <BulkPasswordFootnote lastEvent={pwdLog} />
              </div>
              <button
                onClick={() => setPwdOpen(true)}
                data-testid="dash-bulk-set-passwords"
                className="shrink-0 rounded-none bg-neutral-900 text-white hover:bg-neutral-800 text-xs font-mono uppercase tracking-wider px-4 py-2"
              >
                Open panel
              </button>
            </div>
          </section>
        )}

        <BulkPasswordDialog
          open={pwdOpen}
          onClose={() => setPwdOpen(false)}
          onSaved={loadPwdLog}
        />

        {/* Recent */}
        <section>
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-eyebrow">Latest activity</div>
              <h2 className="font-display text-2xl tracking-tight mt-1">Recent records</h2>
            </div>
            <Link
              to="/records"
              className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4 hover:text-neutral-500"
              data-testid="link-view-all-records"
            >
              View all <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="border border-neutral-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr className="text-left">
                  {["Engineer", "Region", "Parts Taken", "Taken At", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 label-eyebrow">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>
                )}
                {!loading && recent.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">No records yet. Create one on the Records page.</td></tr>
                )}
                {recent.map((r) => (
                  <tr key={r.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                    <td className="px-4 py-3 font-medium">{r.engineer_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.region}</td>
                    <td className="px-4 py-3 text-neutral-600">{r.parts_taken.length} item(s)</td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-600">{fmtDateTime(r.taken_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${r.status === "open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}


// -------------------------------------------------------------------------
// My Requests — Technician's status tracker on the home screen
// -------------------------------------------------------------------------
function MyInventoryCard({ inventory, loading }) {
  const inHand = inventory?.in_hand || [];
  const pending = inventory?.pending_return || [];
  const totalHand = inHand.reduce((s, r) => s + (r.qty || 0), 0);
  const totalPending = pending.reduce((s, r) => s + (r.qty || 0), 0);

  return (
    <section id="my-inventory" data-testid="my-inventory">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="label-eyebrow">My Parts</div>
          <div className="text-xs text-neutral-500 mt-1">
            In Hand stays with you until the storekeeper confirms physical receipt.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* In Hand */}
        <div className="border border-neutral-200 bg-white p-4" data-testid="my-in-hand">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">In Hand</div>
            <div className="font-display text-2xl leading-none">{totalHand}</div>
          </div>
          {loading && <div className="text-[11px] text-neutral-400 mt-3">Loading…</div>}
          {!loading && inHand.length === 0 && (
            <div className="text-[11px] text-neutral-400 mt-3">No parts on you right now.</div>
          )}
          {!loading && inHand.length > 0 && (
            <ul className="mt-3 divide-y divide-neutral-100">
              {inHand.map((p) => (
                <li key={`h:${p.stock_code || p.name}`} className="flex items-center justify-between py-1.5 text-sm">
                  <div className="min-w-0 pr-2">
                    <div className="truncate font-medium">{p.name}</div>
                    {p.stock_code && <div className="text-[10px] font-mono text-neutral-500 truncate">{p.stock_code}</div>}
                  </div>
                  <span className="font-mono">{p.qty}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Pending Physical Return */}
        <div className="border border-neutral-200 bg-white p-4" data-testid="my-pending-return">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Pending Physical Return</div>
            <div className="font-display text-2xl leading-none">{totalPending}</div>
          </div>
          {loading && <div className="text-[11px] text-neutral-400 mt-3">Loading…</div>}
          {!loading && pending.length === 0 && (
            <div className="text-[11px] text-neutral-400 mt-3">Nothing keyed for return.</div>
          )}
          {!loading && pending.length > 0 && (
            <ul className="mt-3 divide-y divide-neutral-100">
              {pending.map((p) => (
                <li key={`p:${p.stock_code || p.name}`} className="flex items-center justify-between py-1.5 text-sm">
                  <div className="min-w-0 pr-2">
                    <div className="truncate font-medium">{p.name}</div>
                    <div className="text-[10px] font-mono text-neutral-500 truncate">
                      {p.stage === "approved" ? "Approved · awaiting storekeeper" : "Awaiting approval"}
                      {p.receipts?.length ? ` · ${p.receipts.join(", ")}` : ""}
                    </div>
                  </div>
                  <span className="font-mono">{p.qty}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}



function MyOpenIVsCard({ records, loading }) {
  return (
    <section id="my-open-ivs" data-testid="my-open-ivs">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="label-eyebrow">Open IVs</div>
          <div className="text-xs text-neutral-500 mt-1">
            Tap Submit Return when you're heading back to the store with parts.
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {loading && (
          <div className="border border-neutral-200 bg-white p-6 text-center text-xs text-neutral-400">Loading…</div>
        )}
        {!loading && (records?.length || 0) === 0 && (
          <div className="border border-neutral-200 bg-white p-6 text-center text-xs text-neutral-400">
            No open issue vouchers.
          </div>
        )}
        {!loading && records.map((r) => (
          <div key={r.id} className="border border-neutral-200 bg-white p-3 flex items-center justify-between gap-3" data-testid={`open-iv-card-${r.id}`}>
            <div className="min-w-0">
              <div className="font-mono text-xs font-semibold">{r.receipt_no}</div>
              <div className="text-[11px] text-neutral-500 mt-0.5 truncate">
                {(r.parts_taken || []).slice(0, 3).map((p) => `${p.name} × ${p.quantity}`).join(" · ")}
                {(r.parts_taken || []).length > 3 ? " · …" : ""}
              </div>
              <div className="text-[10px] font-mono text-neutral-400 mt-0.5">
                {r.work_order_id ? `WO ${r.work_order_id}` : ""}{r.customer_name ? ` · ${r.customer_name}` : ""}
              </div>
            </div>
            <Link
              to={`/records?open_return=${r.id}`}
              data-testid={`open-iv-submit-return-${r.id}`}
              className="shrink-0 inline-flex items-center gap-1 h-9 px-3 border-2 border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-900 hover:text-white text-[11px] font-mono uppercase tracking-wider"
            >
              <ArrowClockwiseIcon size={12} weight="bold" /> Submit Return
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}



function MyRequestsTracker({ items, loading }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      const wo = (r.work_order_id || "").toLowerCase();
      const cust = (r.customer_name || "").toLowerCase();
      const reqNo = (r.request_no || "").toLowerCase();
      return wo.includes(q) || cust.includes(q) || reqNo.includes(q);
    });
  }, [items, search]);

  const buckets = {
    ready:    filtered.filter((r) => (r.status === "approved" || r.status === "partial") && !!r.linked_receipt_no),
    verified: filtered.filter((r) => r.status === "verified"),
    pending:  filtered.filter((r) => r.status === "pending"),
    partial:  filtered.filter((r) => r.status === "partial" && !r.linked_receipt_no),
    rejected: filtered.filter((r) => r.status === "rejected"),
  };

  return (
    <section id="my-requests" data-testid="my-requests-tracker">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="label-eyebrow">My Requests</div>
          <div className="text-xs text-neutral-500 mt-1">Grey → Yellow → Green. Helpdesk verifies first, then Storekeeper releases your IV.</div>
        </div>
        <Link to="/requests" className="text-xs font-mono uppercase tracking-wider text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
          View all <ArrowUpRight size={12} weight="bold" />
        </Link>
      </div>

      {/* Search bar */}
      <div className="relative mb-3">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search your requests by Work Order ID, Customer, or REQ number…"
          className="w-full h-10 pl-9 pr-3 border border-neutral-300 focus:outline-none focus:border-neutral-900 text-sm font-mono rounded-none"
          data-testid="my-req-search"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono uppercase tracking-wider text-neutral-500 hover:text-neutral-900 px-2 py-1"
            data-testid="my-req-search-clear"
          >
            Clear
          </button>
        )}
      </div>

      {/* Summary counter row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="my-req-summary">
        <TrackerStat label="Awaiting verify"   count={buckets.pending.length}  tone="slate"   icon={Hourglass} />
        <TrackerStat label="Verified — awaiting release" count={buckets.verified.length} tone="amber" icon={CheckCircle} />
        <TrackerStat label="Ready for pickup"  count={buckets.ready.length}    tone="emerald" icon={CheckCircle} />
        <TrackerStat label="Partial (needs SK)" count={buckets.partial.length} tone="blue"    icon={TrayArrowDown} />
        <TrackerStat label="Rejected"          count={buckets.rejected.length} tone="red"     icon={XCircle} />
      </div>

      {/* The list */}
      <div className="border border-neutral-200 bg-white">
        {loading && (
          <div className="p-6 text-xs text-center text-neutral-400">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center">
            <div className="text-sm text-neutral-500">You haven't raised any parts requests yet.</div>
            <div className="text-xs text-neutral-400 mt-1">Ask the helpdesk or storekeeper to raise one for you.</div>
          </div>
        )}
        {!loading && items.length > 0 && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-neutral-500">
            No matches for <span className="font-mono">"{search}"</span>. Try a different Work Order or customer.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="divide-y divide-neutral-100">
            {filtered.map((r) => <RequestRow key={r.id} r={r} />)}
          </ul>
        )}
      </div>
    </section>
  );
}


function TrackerStat({ label, count, tone, icon: Icon }) {
  const tones = {
    emerald: "border-emerald-600 bg-emerald-50 text-emerald-900",
    amber:   "border-amber-500  bg-amber-50   text-amber-900",
    slate:   "border-neutral-500 bg-neutral-100 text-neutral-800",
    blue:    "border-blue-600   bg-blue-50    text-blue-900",
    red:     "border-red-600    bg-red-50     text-red-900",
  };
  return (
    <div
      className={`border-l-4 border border-neutral-200 bg-white p-4 flex items-center gap-3 ${count > 0 ? tones[tone] : "text-neutral-400"}`}
      data-testid={`tracker-${tone}`}
    >
      <Icon size={22} weight="duotone" />
      <div>
        <div className="font-display text-3xl leading-none">{count}</div>
        <div className="text-[10px] font-mono uppercase tracking-wider mt-1 opacity-80">{label}</div>
      </div>
    </div>
  );
}


function RequestRow({ r }) {
  const ready = (r.status === "approved" || r.status === "partial") && !!r.linked_receipt_no;
  const s = ready ? "ready" : r.status;
  const cfg = {
    ready:    { label: "Ready for Pickup",    cls: "border-emerald-600 bg-emerald-600 text-white" },
    verified: { label: "Verified — Awaiting Release", cls: "border-amber-500 bg-amber-500 text-white" },
    pending:  { label: "Awaiting Verify",     cls: "border-neutral-500 bg-neutral-500 text-white" },
    partial:  { label: "Partial",             cls: "border-blue-600   bg-blue-600   text-white" },
    approved: { label: "Approved",            cls: "border-emerald-600 bg-emerald-600 text-white" },
    rejected: { label: "Rejected",            cls: "border-red-600    bg-red-600    text-white" },
  }[s] || { label: s, cls: "border-neutral-500 bg-neutral-500 text-white" };

  const created = fmtDateTime(r.created_at);

  return (
    <li className="px-4 py-3 flex items-start gap-4 hover:bg-neutral-50/60" data-testid={`my-req-${r.id}`}>
      <div className="w-24 shrink-0">
        <div className="font-mono text-xs">{r.request_no || r.id.slice(0, 8)}</div>
        <div className="text-[10px] text-neutral-400 mt-0.5">{created}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${cfg.cls}`}>
            {cfg.label}
          </span>
          <AgingPill req={r} testid={`my-req-aging-${r.id}`} />
          {r.work_order_id && (
            <span className="text-[10px] font-mono text-neutral-500">WO {r.work_order_id}</span>
          )}
          {r.customer_name && (
            <span className="text-[10px] font-mono text-neutral-500">· {r.customer_name}</span>
          )}
        </div>
        <div className="text-xs text-neutral-700 mt-1 truncate">
          {(r.items || []).map((it) => `${it.quantity}× ${it.name}`).join(", ") || "—"}
        </div>
        {ready && (
          <div className="text-[11px] text-emerald-800 mt-1 font-medium">
            ✓ Store issued IV <span className="font-mono">{r.linked_receipt_no}</span> — collect from storekeeper.
          </div>
        )}
        {s === "rejected" && r.decision_notes && (
          <div className="text-[11px] text-red-800 mt-1">Reason: {r.decision_notes}</div>
        )}
      </div>
    </li>
  );
}


// -------------------------------------------------------------------------
// Manager Aging Card — surfaces REQs still stuck in grey/yellow.
// -------------------------------------------------------------------------
function ReadyToReleaseCard({ items, loading, storekeeper }) {
  const count = items?.length || 0;
  return (
    <section data-testid="ready-to-release-card">
      <div className="label-eyebrow mb-3">{storekeeper ? "Your Queue" : "Outbound Queue"}</div>
      <div className="border border-neutral-900 bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <Truck size={24} weight="duotone" className="text-emerald-700" />
            <div>
              <div className="font-display text-xl tracking-tight leading-none">Ready to Release</div>
              <div className="text-xs text-neutral-500 mt-1 font-mono uppercase tracking-wider">
                Delivery Orders approved &amp; awaiting release
              </div>
            </div>
          </div>
          <a href="/delivery-orders?status=approved"
            className="h-9 px-4 flex items-center gap-2 border border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white font-mono text-[11px] uppercase tracking-wider"
            data-testid="ready-to-release-open-queue">
            Open queue <ArrowUpRight size={12} weight="bold" />
          </a>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-neutral-500 font-mono">Loading…</div>
        ) : count === 0 ? (
          <div className="p-6 text-sm text-neutral-500">
            <CheckCircle size={16} weight="duotone" className="inline-block mr-1 text-emerald-700" />
            All clear — no approved DOs are waiting for release right now.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 divide-x divide-neutral-200 border-b border-neutral-200">
              <div className="px-6 py-4">
                <div className="label-eyebrow">Awaiting Release</div>
                <div className="font-display text-4xl mt-1 tracking-tight">{count}</div>
              </div>
              <div className="px-6 py-4">
                <div className="label-eyebrow">Total Pieces</div>
                <div className="font-display text-4xl mt-1 tracking-tight">
                  {items.reduce((s, d) => s + (d.parts || []).reduce((x, p) => x + (p.quantity || 0), 0), 0)}
                </div>
              </div>
              <div className="px-6 py-4">
                <div className="label-eyebrow">Oldest</div>
                <div className="font-display text-2xl mt-1 tracking-tight">
                  {(() => {
                    const oldest = items.reduce((a, b) =>
                      new Date(a.approved_at || a.created_at) < new Date(b.approved_at || b.created_at) ? a : b,
                    );
                    const hrs = Math.max(0, Math.round((Date.now() - new Date(oldest.approved_at || oldest.created_at)) / 3600000));
                    return hrs < 24 ? `${hrs}h` : `${Math.round(hrs / 24)}d`;
                  })()}
                </div>
              </div>
            </div>
            <ul className="divide-y divide-neutral-100">
              {items.slice(0, 5).map((d) => {
                const totalQty = (d.parts || []).reduce((s, p) => s + (p.quantity || 0), 0);
                const hrs = Math.max(0, Math.round((Date.now() - new Date(d.approved_at || d.created_at)) / 3600000));
                const ageLabel = hrs < 24 ? `${hrs}h` : `${Math.round(hrs / 24)}d`;
                const stale = hrs >= 48;
                return (
                  <li key={d.id} className="px-6 py-3 flex items-center justify-between gap-4" data-testid={`ready-do-${d.do_no}`}>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono font-semibold">{d.do_no}</span>
                        <span className="text-sm text-neutral-800 truncate">→ {d.customer_name}</span>
                      </div>
                      <div className="text-[11px] font-mono text-neutral-500 mt-0.5 flex items-center gap-2">
                        {d.parts?.length || 0} line(s) · {totalQty} unit(s)
                        · approved by {d.approved_by_name || "?"}
                        <span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                          stale ? "bg-red-100 text-red-800 border border-red-300" : "bg-amber-100 text-amber-900"
                        }`}>{ageLabel} old</span>
                      </div>
                    </div>
                    <a href={`/delivery-orders?status=approved#${d.do_no}`}
                      className="h-8 px-3 flex items-center border border-neutral-900 text-xs font-mono uppercase tracking-wider hover:bg-neutral-900 hover:text-white shrink-0"
                      data-testid={`ready-do-open-${d.do_no}`}>
                      Release →
                    </a>
                  </li>
                );
              })}
            </ul>
            {count > 5 && (
              <div className="px-6 py-2 border-t border-neutral-200 bg-neutral-50 text-[11px] font-mono text-neutral-500 uppercase tracking-wider text-center">
                + {count - 5} more waiting …
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}


// Manager Aging Card — surfaces REQs still stuck in grey/yellow.
// -------------------------------------------------------------------------
function ManagerAgingCard({ items, loading }) {
  const rows = useMemo(() => {
    return (items || [])
      .map((r) => ({ r, ag: computeAging(reqAgingSince(r)) }))
      .filter((x) => x.ag.tone === "warn" || x.ag.tone === "overdue")
      .sort((a, b) => b.ag.hours - a.ag.hours);
  }, [items]);

  const overdue = rows.filter((x) => x.ag.tone === "overdue");
  const warn    = rows.filter((x) => x.ag.tone === "warn");

  return (
    <section data-testid="manager-aging-card">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <FireSimple size={12} weight="bold" className="text-red-700" /> Verify SLA — Aging Requests
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            Grey = waiting for helpdesk verify. Yellow = verified but not yet released. Fresh &lt; 24h · Warn 24–72h · Overdue &gt; 72h.
          </div>
        </div>
        <Link to="/requests" className="text-xs font-mono uppercase tracking-wider text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
          Open Requests <ArrowUpRight size={12} weight="bold" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className={`border-l-4 border p-4 ${overdue.length ? "border-red-600 bg-red-50 text-red-900" : "border-neutral-200 bg-white text-neutral-400"}`} data-testid="aging-overdue-count">
          <div className="flex items-center gap-2">
            <FireSimple size={20} weight="duotone" />
            <div>
              <div className="font-display text-3xl leading-none">{overdue.length}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider mt-1 opacity-80">Overdue &gt; 72h</div>
            </div>
          </div>
        </div>
        <div className={`border-l-4 border p-4 ${warn.length ? "border-amber-500 bg-amber-50 text-amber-900" : "border-neutral-200 bg-white text-neutral-400"}`} data-testid="aging-warn-count">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="duotone" />
            <div>
              <div className="font-display text-3xl leading-none">{warn.length}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider mt-1 opacity-80">Warning 24–72h</div>
            </div>
          </div>
        </div>
      </div>
      <div className="border border-neutral-200 bg-white">
        {loading ? (
          <div className="p-6 text-xs text-center text-neutral-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center">
            <div className="text-sm text-emerald-800 font-medium">✓ Nothing rotting — every REQ is within SLA.</div>
            <div className="text-xs text-neutral-500 mt-1">Aging appears once a REQ waits more than 24h without progressing.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                {["REQ #", "Engineer", "Stage", "Waiting", "Items"].map((h) => (
                  <th key={h} className="px-3 py-2 label-eyebrow">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map(({ r, ag }) => (
                <tr
                  key={r.id}
                  className={`border-b border-neutral-100 last:border-0 ${ag.tone === "overdue" ? "bg-red-50/40" : ""}`}
                  data-testid={`aging-row-${r.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                    <Link to="/requests" className="underline underline-offset-2 hover:text-neutral-500">{r.request_no}</Link>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.for_engineer_name}</div>
                    <div className="text-[10px] text-neutral-500 font-mono">{r.region}{r.customer_name ? ` · ${r.customer_name}` : ""}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${r.status === "pending" ? "bg-neutral-200 text-neutral-800" : "bg-amber-100 text-amber-900"}`}>
                      {r.status === "pending" ? "Pending Verify" : "Verified · Awaiting Release"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <AgingPill req={r} testid={`aging-pill-${r.id}`} />
                  </td>
                  <td className="px-3 py-2 text-neutral-600 text-xs truncate max-w-xs">
                    {(r.items || []).map((it) => `${it.quantity}× ${it.name}`).join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length > 20 && (
          <div className="px-3 py-2 text-[10px] text-neutral-500 font-mono border-t border-neutral-100">
            Showing 20 of {rows.length}. Open the Requests page to see the rest.
          </div>
        )}
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// Password change footnote — shown under the Bulk Set Passwords card.
// -------------------------------------------------------------------------
function BulkPasswordFootnote({ lastEvent }) {
  if (!lastEvent || !lastEvent.created_at) {
    return (
      <div className="mt-3 text-[11px] font-mono uppercase tracking-wider text-neutral-400" data-testid="pwd-footnote-empty">
        No bulk-set on record yet
      </div>
    );
  }
  const { hours, tone, label } = computeAging(lastEvent.created_at);
  const cls =
    tone === "overdue" ? "text-red-700 border-red-600 bg-red-50"
    : tone === "warn"  ? "text-amber-800 border-amber-500 bg-amber-50"
    : "text-emerald-800 border-emerald-600 bg-emerald-50";
  const when = fmtDateTime(lastEvent.created_at);
  const method = lastEvent.action === "user.bulk_password_csv" ? "CSV upload" : "Apply-to-All";
  return (
    <div
      className={`mt-3 inline-flex items-center gap-2 border-l-4 border pl-2 pr-3 py-1 text-[11px] ${cls}`}
      data-testid="pwd-footnote"
      title={lastEvent.summary}
    >
      <ClockCounterClockwise size={12} weight="bold" />
      <span className="font-mono uppercase tracking-wider">Last rotation</span>
      <span>·</span>
      <span><b>{label}</b> ago</span>
      <span className="opacity-60">({when})</span>
      <span className="opacity-60">·</span>
      <span>{method}</span>
      {lastEvent.actor_name && (
        <>
          <span className="opacity-60">·</span>
          <span>by {lastEvent.actor_name}</span>
        </>
      )}
    </div>
  );
}

